import { customerServicePermissionsSchema } from './service-permissions.js';
import { z } from 'zod';
import { limitOverridesSchema, planFeaturesSchema, planLimitsSchema } from './limits.js';
import { overusePolicySchema } from './quota.js';
import { planPoliciesSchema, policyOverridesSchema } from './service-policies.js';
import { USER_ROLES } from './roles.js';

// ── Auth ────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** First-run setup: creates the initial administrator account. */
export const setupSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(12, 'Password must be at least 12 characters').max(256),
});
export type SetupInput = z.infer<typeof setupSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12).max(256),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const requestPasswordResetSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
});
export const completePasswordResetSchema = z.object({
  token: z.string().min(32).max(256),
  newPassword: z.string().min(12).max(256),
});
export const temporaryPasswordSchema = z.object({
  password: z.string().min(12).max(256),
});
export const inviteClientUserSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  accountAdmin: z.boolean().default(true),
});

// ── Two-factor authentication (G17) ─────────────────────────────────

/**
 * Second login step: a 6-digit TOTP code or a one-time recovery code.
 * Deliberately loose (the endpoint decides which it is) so both paths share
 * the same generic error posture as the password step.
 */
export const loginTotpSchema = z.object({
  code: z.string().trim().min(1).max(64),
});
export type LoginTotpInput = z.infer<typeof loginTotpSchema>;

/** Confirm enrolment: a TOTP code proving the authenticator is set up. */
export const totpConfirmSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});
export type TotpConfirmInput = z.infer<typeof totpConfirmSchema>;

/** Disabling 2FA or regenerating recovery codes re-authenticates with the password. */
export const twoFactorReauthSchema = z.object({
  password: z.string().min(1),
});
export type TwoFactorReauthInput = z.infer<typeof twoFactorReauthSchema>;

/** Admin policy: which roles must have 2FA enrolled. */
export const twoFactorPolicySchema = z.object({
  roles: z.array(z.enum(USER_ROLES)),
});
export type TwoFactorPolicyInput = z.infer<typeof twoFactorPolicySchema>;

// ── Users (panel operators) ─────────────────────────────────────────

export const createUserSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(12).max(256),
  role: z.enum(USER_ROLES),
  accountAdmin: z.boolean().default(false),
  servicePermissions: customerServicePermissionsSchema.default([]),
  clientId: z.string().uuid().nullish(),
  /** Set for the `reseller` role: which reseller this login operates (G20). */
  resellerId: z.string().uuid().nullish(),
  /** Treat `password` as temporary: force a change at first login (G15). */
  mustChangePassword: z.boolean().default(false),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: z.enum(USER_ROLES).optional(),
  accountAdmin: z.boolean().optional(),
  servicePermissions: customerServicePermissionsSchema.optional(),
  isActive: z.boolean().optional(),
  password: z.string().min(12).max(256).optional(),
  mustChangePassword: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ── Clients ─────────────────────────────────────────────────────────

export const CLIENT_STATUSES = ['active', 'suspended', 'archived'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const createClientSchema = z.object({
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().max(200).nullish(),
  email: z.string().trim().toLowerCase().email(),
  phone: z.string().trim().max(50).nullish(),
  notes: z.string().max(5000).nullish(),
  /**
   * Owning reseller (G20). Only admin/staff may set it; a reseller's own
   * creations are stamped server-side and this is ignored for them. `null` or
   * omitted = admin-owned.
   */
  ownerId: z.string().uuid().nullish(),
});
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const updateClientSchema = createClientSchema.partial().extend({
  status: z.enum(CLIENT_STATUSES).optional(),
});
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

// ── Service plans ───────────────────────────────────────────────────

export const createPlanSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullish(),
  limits: planLimitsSchema,
  features: planFeaturesSchema,
  /** Calendar, transfer and soft-threshold semantics (V2-G7). */
  policies: planPoliciesSchema.default({}),
  /** What happens when measured usage exceeds a quota (ADR-0014). */
  overusePolicy: overusePolicySchema.default('notify'),
  isActive: z.boolean().default(true),
});
export type CreatePlanInput = z.infer<typeof createPlanSchema>;

export const updatePlanSchema = createPlanSchema.partial();
export type UpdatePlanInput = z.infer<typeof updatePlanSchema>;

// ── Subscriptions ───────────────────────────────────────────────────

export const SUBSCRIPTION_STATUSES = ['active', 'suspended', 'terminated'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const createSubscriptionSchema = z.object({
  clientId: z.string().uuid(),
  planId: z.string().uuid(),
  name: z.string().trim().min(1).max(200),
  /** Provision a dedicated Coolify project for this subscription. */
  createCoolifyProject: z.boolean().default(true),
});
export type CreateSubscriptionInput = z.infer<typeof createSubscriptionSchema>;

export const updateSubscriptionSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  planId: z.string().uuid().optional(),
  status: z.enum(SUBSCRIPTION_STATUSES).optional(),
  limitOverrides: limitOverridesSchema.nullable().optional(),
  policyOverrides: policyOverridesSchema.nullable().optional(),
});
export type UpdateSubscriptionInput = z.infer<typeof updateSubscriptionSchema>;

// ── Resources ───────────────────────────────────────────────────────

export const RESOURCE_KIND_VALUES = ['application', 'database', 'service'] as const;

export const createResourceSchema = z.object({
  kind: z.enum(RESOURCE_KIND_VALUES),
  name: z.string().trim().min(1).max(200),
  /** Link to an existing Coolify resource, when known. */
  coolifyUuid: z.string().trim().min(1).max(100).nullish(),
  details: z.record(z.unknown()).nullish(),
});
export type CreateResourceInput = z.infer<typeof createResourceSchema>;

// ── Settings ────────────────────────────────────────────────────────

export const coolifySettingsSchema = z.object({
  baseUrl: z
    .string()
    .trim()
    .url()
    .transform((u) => u.replace(/\/+$/, '')),
  /** Coolify API token. Omit to keep the currently stored token. */
  apiToken: z.string().trim().min(1).optional(),
});
export type CoolifySettingsInput = z.infer<typeof coolifySettingsSchema>;

export const generalSettingsSchema = z.object({
  panelName: z.string().trim().min(1).max(120).optional(),
});
export type GeneralSettingsInput = z.infer<typeof generalSettingsSchema>;

export const instanceUpdatesSettingsSchema = z.object({
  hostServerUuid: z.string().trim().min(1).max(100).nullable().optional(),
  automaticCoolifyUpdates: z.boolean().optional(),
  automaticHostReboot: z.boolean().optional(),
  preUpdateBackup: z.boolean().optional(),
  maintenanceWindow: z.object({
    start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  }).nullable().optional(),
});
export type InstanceUpdatesSettingsInput = z.infer<typeof instanceUpdatesSettingsSchema>;

export const hostAutomaticUpdatesSchema = z.object({ enabled: z.boolean() });
export const hostPackageInstallSchema = z.object({
  packages: z.array(z.string().regex(/^[a-z0-9](?:[a-z0-9+.-]*[a-z0-9])?(?::[a-z0-9-]+)?$/).max(120)).min(1).max(100),
}).refine((value) => new Set(value.packages).size === value.packages.length, 'Duplicate package names are not allowed');

// ── Modules ─────────────────────────────────────────────────────────

export const toggleModuleSchema = z.object({
  enabled: z.boolean(),
});
export type ToggleModuleInput = z.infer<typeof toggleModuleSchema>;

// ── Pagination ──────────────────────────────────────────────────────

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationInput = z.infer<typeof paginationSchema>;
